import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import {
  Button,
  Input,
  Textarea,
  Select,
  Card,
  Badge,
  FileUpload,
} from "../components/ui";
import { ConfirmModal } from "../components/ui/Modal";

// Opzioni per select
const STATO_OPTIONS = [
  { value: 100000000, label: "Attivo" },
  { value: 100000001, label: "In scadenza" },
  { value: 100000002, label: "Scaduto" },
  { value: 100000003, label: "Annullato" },
];

const TIPO_IMPORTO_OPTIONS = [
  { value: 100000000, label: "Fisso annuale" },
  { value: 100000001, label: "A consumo" },
  { value: 100000002, label: "Misto" },
];

const MODALITA_RINNOVO_OPTIONS = [
  { value: 100000000, label: "Tacito rinnovo" },
  { value: 100000001, label: "Rinnovo esplicito" },
  { value: 100000002, label: "Nessun rinnovo" },
];

const BASE_PAGAMENTO_OPTIONS = [
  { value: 100000000, label: "Data fattura" },
  { value: 100000001, label: "Fine mese" },
  { value: 100000002, label: "Fine mese successivo" },
];

const statoLabels = {
  100000000: { label: "Attivo", variant: "success" },
  100000001: { label: "In scadenza", variant: "warning" },
  100000002: { label: "Scaduto", variant: "danger" },
  100000003: { label: "Annullato", variant: "default" },
};

function DettaglioContrattoPage() {
  const { id } = useParams();
  const { user, apiRequest, canPerformAction } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [contratto, setContratto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({});

  // Dati per select
  const [fornitori, setFornitori] = useState([]);
  const [rsaList, setRsaList] = useState([]);
  const [tipologieFornitura, setTipologieFornitura] = useState([]);

  // Form data (per edit mode)
  const [formData, setFormData] = useState({});

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Document upload
  const [documentiNuovi, setDocumentiNuovi] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(null);

  // Documenti allegati al contratto
  const [documenti, setDocumenti] = useState([]);
  const [documentiLoading, setDocumentiLoading] = useState(false);

  // Check permissions
  const isAdmin = user?.ruolo === 100000000;
  // Era `isAdmin || isRspp`: la scrittura contratti non e' piu' legata al ruolo RSPP ma
  // all'azione amministrabile da /admin/permessi-ruoli (backend: POST/PUT /api/contratti).
  const canEdit = canPerformAction("contratti.gestisci");
  const canDelete = isAdmin;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [contrattoRes, fornitoriRes, rsaRes, tipologieRes] =
          await Promise.all([
            apiRequest(`/contratti/${id}`),
            apiRequest("/fornitori"),
            apiRequest("/rsa"),
            apiRequest("/tipologie-fornitura"),
          ]);

        setContratto(contrattoRes);
        setFornitori(fornitoriRes.value || []);
        setRsaList(rsaRes.value || []);
        setTipologieFornitura(tipologieRes.value || []);

        // Initialize form data
        setFormData({
          nome: contrattoRes.nome || "",
          codice: contrattoRes.codice || "",
          fornitoreId: contrattoRes.fornitore?.id || "",
          rsaId: contrattoRes.rsaId || "",
          tipologiaFornituraId: contrattoRes.tipologiaFornituraId || "",
          dataDecorrenza: contrattoRes.dataDecorrenza?.split("T")[0] || "",
          dataFine: contrattoRes.dataFine?.split("T")[0] || "",
          stato: contrattoRes.stato || 100000000,
          importoAnnuo: contrattoRes.importoAnnuo || "",
          importoMassimo: contrattoRes.importoMassimo || "",
          tipoImporto: contrattoRes.tipoImporto || "",
          giorniPagamento: contrattoRes.giorniPagamento || "",
          basePagamento: contrattoRes.basePagamento || "",
          modalitaRinnovo: contrattoRes.modalitaRinnovo || "",
          giorniPreavviso: contrattoRes.giorniPreavviso || "",
          accordoQuadro: contrattoRes.accordoQuadro || false,
          esclusiva: contrattoRes.esclusiva || false,
          nomeReferente: contrattoRes.nomeReferente || "",
          emailReferente: contrattoRes.emailReferente || "",
          telefonoReferente: contrattoRes.telefonoReferente || "",
          orariPreferenziali: contrattoRes.orariPreferenziali || "",
          clausoleParticolari: contrattoRes.clausoleParticolari || "",
          noteSla: contrattoRes.noteSla || "",
          note: contrattoRes.note || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast("Errore nel caricamento del contratto", "error");
        navigate("/contratti");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, apiRequest, navigate, showToast]);

  // Fetch documenti associati al contratto
  useEffect(() => {
    if (!id) return;
    const fetchDocumenti = async () => {
      setDocumentiLoading(true);
      try {
        const result = await apiRequest(`/documenti?contrattoId=${id}`);
        setDocumenti(result.value || []);
      } catch (error) {
        console.error("Error fetching documenti contratto:", error);
      } finally {
        setDocumentiLoading(false);
      }
    };
    fetchDocumenti();
  }, [id, apiRequest]);

  const handlePreviewDoc = async (doc) => {
    try {
      const result = await apiRequest(`/documenti/${doc.id}/preview`);
      const previewUrl = result.previewUrl || result.url;
      if (previewUrl) {
        window.open(previewUrl, "_blank");
      }
    } catch (error) {
      console.error("Error getting preview:", error);
      showToast("Errore nel caricamento dell'anteprima", "error");
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      if (doc.urlSharepoint) {
        window.open(doc.urlSharepoint, "_blank");
      } else {
        const response = await apiRequest(`/documenti/${doc.id}/download`);
        if (typeof response === "string") {
          window.open(response, "_blank");
        }
      }
    } catch (error) {
      console.error("Error downloading:", error);
      showToast("Errore nel download del documento", "error");
    }
  };

  const handleUploadDocumenti = async () => {
    if (documentiNuovi.length === 0) return;

    setUploadLoading(true);
    let uploadErrors = 0;
    try {
      for (const file of documentiNuovi) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append("file", file);
          formDataUpload.append("nome", file.name.replace(/\.[^/.]+$/, ""));
          formDataUpload.append("tipoDocumento", "100000000");
          formDataUpload.append(
            "rsaId",
            contratto.rsaId || rsaList[0]?.id || "",
          );
          formDataUpload.append("contrattoId", id);
          formDataUpload.append("visibileFornitore", "true");
          if (contratto.fornitore?.id) {
            formDataUpload.append("fornitoreId", contratto.fornitore.id);
          }
          if (contratto.dataDecorrenza) {
            formDataUpload.append(
              "dataDocumento",
              contratto.dataDecorrenza.split("T")[0],
            );
          }

          await apiRequest("/documenti/upload", {
            method: "POST",
            body: formDataUpload,
            headers: {},
          });
        } catch (err) {
          console.error("Error uploading document:", err);
          uploadErrors++;
        }
      }

      if (uploadErrors > 0) {
        showToast(
          `${documentiNuovi.length - uploadErrors} caricati, ${uploadErrors} errori`,
          "warning",
        );
      } else {
        showToast(
          `${documentiNuovi.length} document${documentiNuovi.length > 1 ? "i caricati" : "o caricato"} con successo`,
          "success",
        );
      }
      setDocumentiNuovi([]);

      // Ricarica lista documenti
      const result = await apiRequest(`/documenti?contrattoId=${id}`);
      setDocumenti(result.value || []);
    } catch (error) {
      console.error("Error uploading documents:", error);
      showToast("Errore nel caricamento dei documenti", "error");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteDocumento = async (docId) => {
    try {
      await apiRequest(`/documenti/${docId}`, { method: "DELETE" });
      showToast("Documento eliminato", "success");
      setDocumenti((prev) => prev.filter((d) => d.id !== docId));
    } catch (error) {
      console.error("Error deleting document:", error);
      showToast("Errore nell'eliminazione del documento", "error");
    } finally {
      setShowDeleteDocModal(null);
    }
  };

  const getFileExtension = (doc) => {
    const url = doc.urlSharepoint || "";
    const nome = doc.nome || "";
    const urlPath = url.split("?")[0];
    const match =
      urlPath.match(/\.([a-zA-Z0-9]+)$/i) || nome.match(/\.([a-zA-Z0-9]+)$/i);
    return match ? match[1].toLowerCase() : "";
  };

  const TIPI_DOCUMENTO_LABELS = {
    100000000: "Contratto firmato",
    100000001: "Appendice contrattuale",
    100000002: "SLA",
    100000003: "Rapportino",
    100000004: "Verbale sopralluogo",
    100000005: "Certificazione",
    100000006: "Polizza assicurativa",
    100000007: "DURC",
    100000008: "Visura camerale",
    100000009: "Documento gara",
    100000010: "Fattura",
    100000011: "Altro",
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = "Il nome del contratto e obbligatorio";
    }
    if (!formData.fornitoreId) {
      newErrors.fornitoreId = "Seleziona un fornitore";
    }
    if (!formData.dataDecorrenza) {
      newErrors.dataDecorrenza = "La data di decorrenza e obbligatoria";
    }
    if (!formData.dataFine) {
      newErrors.dataFine = "La data di fine e obbligatoria";
    }
    if (
      formData.dataDecorrenza &&
      formData.dataFine &&
      formData.dataDecorrenza > formData.dataFine
    ) {
      newErrors.dataFine =
        "La data di fine deve essere successiva alla decorrenza";
    }
    if (
      formData.emailReferente &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailReferente)
    ) {
      newErrors.emailReferente = "Inserisci un indirizzo email valido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      showToast("Correggi gli errori nel form", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: formData.nome.trim(),
        codice: formData.codice.trim() || null,
        fornitoreId: formData.fornitoreId,
        rsaId: formData.rsaId || null,
        tipologiaFornituraId: formData.tipologiaFornituraId || null,
        dataDecorrenza: formData.dataDecorrenza,
        dataFine: formData.dataFine,
        stato: parseInt(formData.stato),
        importoAnnuo: formData.importoAnnuo
          ? parseFloat(formData.importoAnnuo)
          : null,
        importoMassimo: formData.importoMassimo
          ? parseFloat(formData.importoMassimo)
          : null,
        tipoImporto: formData.tipoImporto || null,
        giorniPagamento: formData.giorniPagamento
          ? parseInt(formData.giorniPagamento)
          : null,
        basePagamento: formData.basePagamento || null,
        modalitaRinnovo: formData.modalitaRinnovo || null,
        giorniPreavviso: formData.giorniPreavviso
          ? parseInt(formData.giorniPreavviso)
          : null,
        accordoQuadro: formData.accordoQuadro,
        esclusiva: formData.esclusiva,
        nomeReferente: formData.nomeReferente.trim() || null,
        emailReferente: formData.emailReferente.trim() || null,
        telefonoReferente: formData.telefonoReferente.trim() || null,
        orariPreferenziali: formData.orariPreferenziali.trim() || null,
        clausoleParticolari: formData.clausoleParticolari.trim() || null,
        noteSla: formData.noteSla.trim() || null,
        note: formData.note.trim() || null,
      };

      await apiRequest(`/contratti/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      // Upload documenti se selezionati
      let uploadErrors = 0;
      if (documentiNuovi.length > 0) {
        for (const file of documentiNuovi) {
          try {
            const formDataUpload = new FormData();
            formDataUpload.append("file", file);
            formDataUpload.append("nome", file.name.replace(/\.[^/.]+$/, ""));
            formDataUpload.append("tipoDocumento", "100000000");
            formDataUpload.append(
              "rsaId",
              formData.rsaId || rsaList[0]?.id || "",
            );
            formDataUpload.append("contrattoId", id);
            formDataUpload.append("visibileFornitore", "true");
            if (formData.fornitoreId) {
              formDataUpload.append("fornitoreId", formData.fornitoreId);
            }
            if (formData.dataDecorrenza) {
              formDataUpload.append("dataDocumento", formData.dataDecorrenza);
            }

            await apiRequest("/documenti/upload", {
              method: "POST",
              body: formDataUpload,
              headers: {},
            });
          } catch (uploadError) {
            console.error("Error uploading document:", uploadError);
            uploadErrors++;
          }
        }
        setDocumentiNuovi([]);
      }

      // Reload contratto e documenti
      const [updated, docsResult] = await Promise.all([
        apiRequest(`/contratti/${id}`),
        apiRequest(`/documenti?contrattoId=${id}`),
      ]);
      setContratto(updated);
      setDocumenti(docsResult.value || []);

      if (uploadErrors > 0) {
        showToast(
          "Contratto salvato, ma alcuni documenti non sono stati caricati",
          "warning",
        );
      } else {
        showToast(
          documentiNuovi.length > 0
            ? "Contratto e documenti salvati con successo"
            : "Contratto aggiornato con successo",
          "success",
        );
      }
      setIsEditMode(false);
    } catch (error) {
      console.error("Error updating contratto:", error);
      showToast(error.message || "Errore nell'aggiornamento", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiRequest(`/contratti/${id}`, {
        method: "DELETE",
      });

      showToast("Contratto eliminato", "success");
      navigate("/contratti");
    } catch (error) {
      console.error("Error deleting contratto:", error);
      showToast(error.message || "Errore nell'eliminazione", "error");
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const cancelEdit = () => {
    // Reset form data to original values
    setFormData({
      nome: contratto.nome || "",
      codice: contratto.codice || "",
      fornitoreId: contratto.fornitore?.id || "",
      rsaId: contratto.rsaId || "",
      tipologiaFornituraId: contratto.tipologiaFornituraId || "",
      dataDecorrenza: contratto.dataDecorrenza?.split("T")[0] || "",
      dataFine: contratto.dataFine?.split("T")[0] || "",
      stato: contratto.stato || 100000000,
      importoAnnuo: contratto.importoAnnuo || "",
      importoMassimo: contratto.importoMassimo || "",
      tipoImporto: contratto.tipoImporto || "",
      giorniPagamento: contratto.giorniPagamento || "",
      basePagamento: contratto.basePagamento || "",
      modalitaRinnovo: contratto.modalitaRinnovo || "",
      giorniPreavviso: contratto.giorniPreavviso || "",
      accordoQuadro: contratto.accordoQuadro || false,
      esclusiva: contratto.esclusiva || false,
      nomeReferente: contratto.nomeReferente || "",
      emailReferente: contratto.emailReferente || "",
      telefonoReferente: contratto.telefonoReferente || "",
      orariPreferenziali: contratto.orariPreferenziali || "",
      clausoleParticolari: contratto.clausoleParticolari || "",
      noteSla: contratto.noteSla || "",
      note: contratto.note || "",
    });
    setErrors({});
    setIsEditMode(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("it-IT");
  };

  const formatCurrency = (value) => {
    if (!value) return "-";
    return `€ ${value.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getLabelForValue = (options, value) => {
    const opt = options.find((o) => o.value === value);
    return opt?.label || "-";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!contratto) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Contratto non trovato</p>
        <Button onClick={() => navigate("/contratti")} className="mt-4">
          Torna alla lista
        </Button>
      </div>
    );
  }

  // VIEW MODE
  if (!isEditMode) {
    return (
      <div>
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {contratto.nome}
              </h1>
              <Badge
                variant={statoLabels[contratto.stato]?.variant || "default"}
                dot
              >
                {statoLabels[contratto.stato]?.label || "N/D"}
              </Badge>
            </div>
            <p className="text-gray-600">Codice: {contratto.codice || "N/D"}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/contratti")}>
              Indietro
            </Button>
            {canEdit && (
              <Button onClick={() => setIsEditMode(true)}>Modifica</Button>
            )}
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Informazioni Base */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Informazioni Base
            </h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Fornitore</dt>
                <dd className="font-medium">
                  {contratto.fornitore ? (
                    <Link
                      to={`/fornitori/${contratto.fornitore.id}`}
                      className="text-primary-600 hover:underline"
                    >
                      {contratto.fornitore.nome}
                    </Link>
                  ) : (
                    "-"
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Tipologia Fornitura</dt>
                <dd className="font-medium">
                  {contratto.tipologiaFornitura || "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">RSA</dt>
                <dd className="font-medium">
                  {contratto.rsaId ? (
                    <Link
                      to={`/rsa/${contratto.rsaId}`}
                      className="text-primary-600 hover:underline"
                    >
                      {rsaList.find((r) => r.id === contratto.rsaId)?.nome ||
                        "RSA"}
                    </Link>
                  ) : (
                    "Tutte le RSA"
                  )}
                </dd>
              </div>
            </dl>

            {/* Flags */}
            <div className="flex gap-2 mt-4 pt-4 border-t">
              {contratto.accordoQuadro && (
                <Badge variant="info">Accordo Quadro</Badge>
              )}
              {contratto.esclusiva && (
                <Badge variant="primary">Esclusiva</Badge>
              )}
            </div>
          </Card>

          {/* Date e Durata */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Date e Durata
            </h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Data Decorrenza</dt>
                <dd className="font-medium">
                  {formatDate(contratto.dataDecorrenza)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Data Fine</dt>
                <dd className="font-medium">
                  {formatDate(contratto.dataFine)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Modalita Rinnovo</dt>
                <dd className="font-medium">
                  {getLabelForValue(
                    MODALITA_RINNOVO_OPTIONS,
                    contratto.modalitaRinnovo,
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Giorni Preavviso</dt>
                <dd className="font-medium">
                  {contratto.giorniPreavviso || "-"}
                </dd>
              </div>
            </dl>
          </Card>

          {/* Importi */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Importi e Pagamenti
            </h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Importo Annuo</dt>
                <dd className="font-medium">
                  {formatCurrency(contratto.importoAnnuo)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Importo Massimo</dt>
                <dd className="font-medium">
                  {formatCurrency(contratto.importoMassimo)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Tipo Importo</dt>
                <dd className="font-medium">
                  {getLabelForValue(
                    TIPO_IMPORTO_OPTIONS,
                    contratto.tipoImporto,
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Giorni Pagamento</dt>
                <dd className="font-medium">
                  {contratto.giorniPagamento
                    ? `${contratto.giorniPagamento} gg`
                    : "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Base Pagamento</dt>
                <dd className="font-medium">
                  {getLabelForValue(
                    BASE_PAGAMENTO_OPTIONS,
                    contratto.basePagamento,
                  )}
                </dd>
              </div>
            </dl>
          </Card>

          {/* Referente */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Referente
            </h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Nome</dt>
                <dd className="font-medium">
                  {contratto.nomeReferente || "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Email</dt>
                <dd className="font-medium">
                  {contratto.emailReferente ? (
                    <a
                      href={`mailto:${contratto.emailReferente}`}
                      className="text-primary-600 hover:underline"
                    >
                      {contratto.emailReferente}
                    </a>
                  ) : (
                    "-"
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Telefono</dt>
                <dd className="font-medium">
                  {contratto.telefonoReferente ? (
                    <a
                      href={`tel:${contratto.telefonoReferente}`}
                      className="text-primary-600 hover:underline"
                    >
                      {contratto.telefonoReferente}
                    </a>
                  ) : (
                    "-"
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Orari Preferenziali</dt>
                <dd className="font-medium">
                  {contratto.orariPreferenziali || "-"}
                </dd>
              </div>
            </dl>
          </Card>
        </div>

        {/* Clausole e Note */}
        {(contratto.clausoleParticolari ||
          contratto.noteSla ||
          contratto.note) && (
          <Card className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Clausole e Note
            </h2>
            <div className="space-y-4">
              {contratto.clausoleParticolari && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Clausole Particolari
                  </h3>
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {contratto.clausoleParticolari}
                  </p>
                </div>
              )}
              {contratto.noteSla && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Note SLA
                  </h3>
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {contratto.noteSla}
                  </p>
                </div>
              )}
              {contratto.note && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Note Generali
                  </h3>
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {contratto.note}
                  </p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Documenti Allegati */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Documenti Allegati
          </h2>

          {/* Link diretto urlDocumento (legacy) */}
          {contratto.urlDocumento && !documenti.length && (
            <a
              href={contratto.urlDocumento}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary-600 hover:underline mb-4"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Scarica documento contratto
            </a>
          )}

          {documentiLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinner w-8 h-8"></div>
            </div>
          ) : documenti.length > 0 ? (
            <div className="space-y-3">
              {documenti.map((doc) => {
                const ext = getFileExtension(doc);
                const isPdf = ext === "pdf";
                const tipoLabel =
                  TIPI_DOCUMENTO_LABELS[doc.tipoDocumento] || "Documento";

                return (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                          isPdf
                            ? "bg-red-100 text-red-700"
                            : ext === "doc" || ext === "docx"
                              ? "bg-blue-100 text-blue-700"
                              : ext === "xls" || ext === "xlsx"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {ext.toUpperCase() || "FILE"}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {doc.nome}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>{tipoLabel}</span>
                          {doc.dataDocumento && (
                            <>
                              <span>&middot;</span>
                              <span>
                                {new Date(doc.dataDocumento).toLocaleDateString(
                                  "it-IT",
                                )}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreviewDoc(doc)}
                        title="Visualizza anteprima"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadDoc(doc)}
                        title="Scarica documento"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowDeleteDocModal(doc.id)}
                          title="Elimina documento"
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : !contratto.urlDocumento ? (
            <p className="text-gray-500 text-sm py-4 text-center">
              Nessun documento allegato a questo contratto
            </p>
          ) : null}

          {/* Upload nuovi documenti (in view mode) */}
          {canEdit && (
            <div className={documenti.length > 0 ? "border-t pt-4 mt-4" : ""}>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Aggiungi documenti
              </h3>
              <FileUpload
                accept=".pdf,.doc,.docx"
                maxSize={100}
                multiple
                onChange={(files) => setDocumentiNuovi(files || [])}
                value={documentiNuovi.length > 0 ? documentiNuovi : null}
                helper="Seleziona uno o piu file (PDF, DOC, DOCX - max 100MB ciascuno)"
              />
              {documentiNuovi.length > 0 && (
                <div className="mt-3 flex justify-end">
                  <Button
                    onClick={handleUploadDocumenti}
                    loading={uploadLoading}
                    size="sm"
                  >
                    Carica {documentiNuovi.length} document
                    {documentiNuovi.length > 1 ? "i" : "o"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Actions */}
        {canDelete && (
          <div className="flex justify-end">
            <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
              Elimina Contratto
            </Button>
          </div>
        )}

        {/* Delete Contract Modal */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title="Elimina Contratto"
          message={`Sei sicuro di voler eliminare il contratto "${contratto.nome}"? Questa azione non puo essere annullata.`}
          confirmText="Elimina"
          confirmVariant="danger"
          loading={deleteLoading}
        />

        {/* Delete Document Modal */}
        <ConfirmModal
          isOpen={!!showDeleteDocModal}
          onClose={() => setShowDeleteDocModal(null)}
          onConfirm={() => handleDeleteDocumento(showDeleteDocModal)}
          title="Elimina Documento"
          message="Sei sicuro di voler eliminare questo documento? Questa azione non puo essere annullata."
          confirmText="Elimina"
          confirmVariant="danger"
        />
      </div>
    );
  }

  // EDIT MODE
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Modifica Contratto
          </h1>
          <p className="text-gray-600">{contratto.nome}</p>
        </div>
        <Button variant="outline" onClick={cancelEdit}>
          Annulla
        </Button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        {/* Informazioni Base */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informazioni Base
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome Contratto *"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              error={errors.nome}
            />

            <Input
              label="Codice Contratto"
              name="codice"
              value={formData.codice}
              onChange={handleChange}
            />

            <Select
              label="Fornitore *"
              name="fornitoreId"
              value={formData.fornitoreId}
              onChange={handleChange}
              error={errors.fornitoreId}
              options={[
                { value: "", label: "Seleziona fornitore..." },
                ...fornitori.map((f) => ({ value: f.id, label: f.nome })),
              ]}
            />

            <Select
              label="RSA"
              name="rsaId"
              value={formData.rsaId}
              onChange={handleChange}
              options={[
                { value: "", label: "Tutte le RSA (generale)" },
                ...rsaList.map((r) => ({ value: r.id, label: r.nome })),
              ]}
            />

            <Select
              label="Tipologia Fornitura"
              name="tipologiaFornituraId"
              value={formData.tipologiaFornituraId}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona tipologia..." },
                ...tipologieFornitura.map((t) => ({
                  value: t.id,
                  label: t.nome,
                })),
              ]}
            />

            <Select
              label="Stato"
              name="stato"
              value={formData.stato}
              onChange={handleChange}
              options={STATO_OPTIONS}
            />
          </div>
        </Card>

        {/* Date e Durata */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Date e Durata
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              type="date"
              label="Data Decorrenza *"
              name="dataDecorrenza"
              value={formData.dataDecorrenza}
              onChange={handleChange}
              error={errors.dataDecorrenza}
            />

            <Input
              type="date"
              label="Data Fine *"
              name="dataFine"
              value={formData.dataFine}
              onChange={handleChange}
              error={errors.dataFine}
            />

            <Select
              label="Modalita Rinnovo"
              name="modalitaRinnovo"
              value={formData.modalitaRinnovo}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona..." },
                ...MODALITA_RINNOVO_OPTIONS,
              ]}
            />

            <Input
              type="number"
              label="Giorni Preavviso"
              name="giorniPreavviso"
              value={formData.giorniPreavviso}
              onChange={handleChange}
              min="0"
            />
          </div>
        </Card>

        {/* Importi e Pagamenti */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Importi e Pagamenti
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              type="number"
              label="Importo Annuo (EUR)"
              name="importoAnnuo"
              value={formData.importoAnnuo}
              onChange={handleChange}
              min="0"
              step="0.01"
            />

            <Input
              type="number"
              label="Importo Massimo (EUR)"
              name="importoMassimo"
              value={formData.importoMassimo}
              onChange={handleChange}
              min="0"
              step="0.01"
            />

            <Select
              label="Tipo Importo"
              name="tipoImporto"
              value={formData.tipoImporto}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona..." },
                ...TIPO_IMPORTO_OPTIONS,
              ]}
            />

            <Input
              type="number"
              label="Giorni Pagamento"
              name="giorniPagamento"
              value={formData.giorniPagamento}
              onChange={handleChange}
              min="0"
            />

            <Select
              label="Base Pagamento"
              name="basePagamento"
              value={formData.basePagamento}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona..." },
                ...BASE_PAGAMENTO_OPTIONS,
              ]}
            />
          </div>

          <div className="flex gap-6 mt-4 pt-4 border-t">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="accordoQuadro"
                checked={formData.accordoQuadro}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Accordo Quadro</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="esclusiva"
                checked={formData.esclusiva}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Esclusiva</span>
            </label>
          </div>
        </Card>

        {/* Referente */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Referente Contratto
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Nome Referente"
              name="nomeReferente"
              value={formData.nomeReferente}
              onChange={handleChange}
            />

            <Input
              type="email"
              label="Email Referente"
              name="emailReferente"
              value={formData.emailReferente}
              onChange={handleChange}
              error={errors.emailReferente}
            />

            <Input
              type="tel"
              label="Telefono Referente"
              name="telefonoReferente"
              value={formData.telefonoReferente}
              onChange={handleChange}
            />

            <Input
              label="Orari Preferenziali"
              name="orariPreferenziali"
              value={formData.orariPreferenziali}
              onChange={handleChange}
            />
          </div>
        </Card>

        {/* Clausole e Note */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Clausole e Note
          </h2>

          <div className="space-y-4">
            <Textarea
              label="Clausole Particolari"
              name="clausoleParticolari"
              value={formData.clausoleParticolari}
              onChange={handleChange}
              rows={3}
            />

            <Textarea
              label="Note SLA"
              name="noteSla"
              value={formData.noteSla}
              onChange={handleChange}
              rows={3}
            />

            <Textarea
              label="Note Generali"
              name="note"
              value={formData.note}
              onChange={handleChange}
              rows={3}
            />
          </div>
        </Card>

        {/* Documenti */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Documenti Allegati
          </h2>

          {/* Lista documenti esistenti */}
          {documenti.length > 0 && (
            <div className="space-y-2 mb-4">
              {documenti.map((doc) => {
                const ext = getFileExtension(doc);
                const tipoLabel =
                  TIPI_DOCUMENTO_LABELS[doc.tipoDocumento] || "Documento";
                return (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                          ext === "pdf"
                            ? "bg-red-100 text-red-700"
                            : ext === "doc" || ext === "docx"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {ext.toUpperCase() || "FILE"}
                      </span>
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {doc.nome}
                      </span>
                      <span className="text-xs text-gray-500">{tipoLabel}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreviewDoc(doc)}
                        title="Anteprima"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadDoc(doc)}
                        title="Scarica"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowDeleteDocModal(doc.id)}
                          title="Elimina"
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Upload nuovi documenti */}
          <div className={documenti.length > 0 ? "border-t pt-4" : ""}>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Aggiungi documenti
            </h3>
            <FileUpload
              accept=".pdf,.doc,.docx"
              maxSize={100}
              multiple
              onChange={(files) => setDocumentiNuovi(files || [])}
              value={documentiNuovi.length > 0 ? documentiNuovi : null}
              helper="Seleziona i file e clicca 'Salva Modifiche' per caricarli"
            />
          </div>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={cancelEdit}>
            Annulla
          </Button>
          <Button type="submit" loading={saving}>
            Salva Modifiche
          </Button>
        </div>
      </form>

      {/* Delete Document Modal (edit mode) */}
      <ConfirmModal
        isOpen={!!showDeleteDocModal}
        onClose={() => setShowDeleteDocModal(null)}
        onConfirm={() => handleDeleteDocumento(showDeleteDocModal)}
        title="Elimina Documento"
        message="Sei sicuro di voler eliminare questo documento? Questa azione non puo essere annullata."
        confirmText="Elimina"
        confirmVariant="danger"
      />
    </div>
  );
}

export default DettaglioContrattoPage;
